import { useEffect, useState } from 'react';
import { fetchJSON } from 'api/fetch';

import { PromoCodePlan } from 'features/managePlan/types';

interface PromoCodePlansResponse {
  plans: PromoCodePlan[];
}

interface UsePromoCodePlansReturn {
  plans: PromoCodePlan[];
  loading: boolean;
  error: string | null;
  isAnnualCoupon: boolean;
}

const usePromoCodePlans = (
  promoCode: string | null
): UsePromoCodePlansReturn => {
  const [plans, setPlans] = useState<PromoCodePlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAnnualCoupon, setIsAnnualCoupon] = useState(false);

  useEffect(() => {
    if (!promoCode) {
      setPlans([]);
      setError(null);
      setIsAnnualCoupon(false);
      return;
    }

    const fetchPlans = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = (await fetchJSON(
          `/growth/marketing_discount_coupons/promo_code_plans?promo_code=${encodeURIComponent(
            promoCode
          )}`
        )) as PromoCodePlansResponse;

        const fetchedPlans = response.plans || [];
        setPlans(fetchedPlans);

        // Check if all plans have eligible_plans that contain annual plans
        const annualPlanSuffixes = ['_annual', '_annually'];
        const isAllAnnual =
          fetchedPlans.length > 0 &&
          fetchedPlans.every(plan => {
            if (!plan.eligible_plans || plan.eligible_plans.length === 0) {
              return false;
            }
            return plan.eligible_plans.some(eligiblePlan =>
              annualPlanSuffixes.some(suffix => eligiblePlan.endsWith(suffix))
            );
          });

        setIsAnnualCoupon(isAllAnnual);
      } catch (err) {
        setError('Failed to fetch promo code plans');
        setPlans([]);
        setIsAnnualCoupon(false);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [promoCode]);

  return { plans, loading, error, isAnnualCoupon };
};

export default usePromoCodePlans;
export type { UsePromoCodePlansReturn };
